import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import BrandfetchGlassmorphicDisplay from './BrandfetchGlassmorphicDisplay';
import { clientLogger } from '../utils/logger';
import { authenticatedFetch, syncUserSession } from '../utils/apiUtils';

/**
 * A simplified brands list component that focuses on the glassmorphic view
 */
const SimpleBrandsList = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Do a direct check if the user is authenticated and admin
        const response = await fetch('/api/auth/me');
        if (response.ok) {
          const userData = await response.json();
          if (userData && userData.is_admin) {
            setAuthChecked(true);
            return;
          }
        }
        
        // If we get here, either the user is not logged in or not an admin
        console.error("User not authenticated or not an admin");
        window.location.href = '/admin'; // Use window.location for a hard redirect
      } catch (error) {
        console.error("Auth check failed:", error);
      }
    };
    
    checkAuth();
  }, []);

  useEffect(() => {
    fetchBrands();
  }, []);

  /**
   * Get user ID from localStorage for API requests
   */
  const getUserId = () => {
    const userDataString = localStorage.getItem('userData');
    if (!userDataString) return null;
    
    try {
      const userData = JSON.parse(userDataString);
      return userData.id;
    } catch (e) {
      clientLogger.error('Failed to parse user data from localStorage', e);
      return null;
    }
  };

  /**
   * Fetch brands data from the API
   */
  const fetchBrands = async () => {
    if (!authChecked) {
      // Don't fetch until auth is confirmed
      return;
    }
    
    try {
      setLoading(true);
      const userId = getUserId();
      if (!userId) {
        throw new Error('No user ID available');
      }
      
      await syncUserSession();
      
      const response = await authenticatedFetch(`/api/admin/brands?userId=${userId}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch brands (Status: ${response.status})`);
      }

      const data = await response.json();
      setBrands(data || []);
    } catch (err) {
      clientLogger.error('Error fetching brands:', err);
      setError(err.message);
      toast.error('Failed to load brands data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle opening the glassmorphic modal for a brand
   */
  const openGlassmorphicView = (brand) => {
    setSelectedBrand(brand);
    setShowModal(true);
  };

  /**
   * Handle closing the modal
   */
  const closeModal = () => {
    setSelectedBrand(null);
    setShowModal(false);
  };

  /**
   * Render the brands list
   */
  const renderBrandsList = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {brands.map((brand) => (
        <div 
          key={brand.id} 
          className="bg-white rounded-lg shadow-md p-4 border border-gray-200 hover:shadow-lg transition-shadow"
        >
          <h3 className="text-lg font-semibold mb-2">{brand.name || brand.domain}</h3>
          <p className="text-gray-600 mb-4">{brand.domain}</p>
          
          <button
            onClick={() => openGlassmorphicView(brand)}
            className="w-full bg-gradient-to-r from-teal-500 to-green-500 text-white py-2 px-4 rounded-md hover:from-teal-600 hover:to-green-600 transition-colors flex items-center justify-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm1 0v12h12V4H4z" clipRule="evenodd" />
              <path d="M6 7a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h5a1 1 0 100-2H7z" />
            </svg>
            Glassmorphic View
          </button>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading brands data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6">
          <p>Error: {error}</p>
          <button 
            className="mt-2 text-blue-600 hover:underline"
            onClick={fetchBrands}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Brands Explorer - Glassmorphic View</h1>
        <button 
          onClick={fetchBrands}
          className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md transition-colors"
        >
          Refresh Data
        </button>
      </div>
      
      <p className="mb-6 text-gray-600">
        Select a brand below to view its AI insights and analysis in the new glassmorphic interface.
      </p>
      
      {brands.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-500">No brands found</p>
        </div>
      ) : (
        renderBrandsList()
      )}
      
      {/* Render the glassmorphic modal if a brand is selected */}
      {showModal && selectedBrand && (
        <BrandfetchGlassmorphicDisplay 
          domain={selectedBrand.domain} 
          onClose={closeModal} 
        />
      )}
    </div>
  );
};

export default SimpleBrandsList; 